/**
 * QA Governance API client — Week12 / PC-07.
 *
 * The backend mounts every route under `/api/v1/qa`. The OpenAPI files in each
 * `docs/research/Week12/PC-07-E0N_.../04_API_Data/` folder declare the base URL
 * `/qa/v1`; the service's global prefix supplies the version. ADMIN-only.
 */
import { apiGet, apiPost, apiPut } from '../../../adapters/api-client/api';

const QA = '/api/v1/qa';

// ── PC-07-E01 — verification modes ───────────────────────────────────────────

export type VerificationMode = 'V-1' | 'V-2' | 'V-3' | 'V-4';
export type BuildStatus = 'Done' | 'Partial' | 'Missing' | 'Deferred';

export interface EpicVerification {
  epic_code: string;
  week: number;
  tier: string;
  build_status: BuildStatus;
  gate: 'Manual' | 'Build';
  verification_mode: VerificationMode;
  exit_criteria_set?: string;
  reconciliation_ref?: string;
  planned_cases?: number;
  executed_cases?: number;
  not_built_items?: number;
  mode_override_reason?: string;
  mode_override_approved_by?: string;
}

export interface ScopeSummary {
  v1: number;
  v2: number;
  v3: number;
  v4: number;
  alcance_verificable: number;
  total_epicas: number;
  sin_clasificar: number;
  baseline_firmada: boolean;
  baseline_vigente: boolean;
}

export interface ModeHistoryEntry {
  epic_code: string;
  mode_from: string;
  mode_to: string;
  cause: string;
  actor: string;
  changed_at: string;
}

export const MODE_LABEL: Record<VerificationMode, string> = {
  'V-1': 'Verificar as-built',
  'V-2': 'Verificar parcial + diferimiento',
  'V-3': 'Validación de proceso manual',
  'V-4': 'Sin base ejecutable',
};

export const fetchVerificationModes = (params: { week?: number; mode?: string } = {}) => {
  const qs = new URLSearchParams();
  if (params.week) qs.set('week', String(params.week));
  if (params.mode) qs.set('mode', params.mode);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiGet<EpicVerification[]>(`${QA}/verification-modes${suffix}`);
};

export const fetchVerificationScope = () => apiGet<ScopeSummary>(`${QA}/verification-scope`);

export const fetchModeHistory = (epicCode: string) =>
  apiGet<ModeHistoryEntry[]>(`${QA}/verification-modes/${epicCode}/history`);

export const recalculatePadron = (snapshotId: string, dryRun = false) =>
  apiPost<{ cambiadas: number; sin_cambio: number; sin_clasificar: number }>(
    `${QA}/verification-modes/recalculate`,
    { snapshot_id: snapshotId, dry_run: dryRun },
  );

export const publishPadron = () => apiPost(`${QA}/verification-padron/publish`, {});

// ── PC-07-E02 — scope rebaseline ─────────────────────────────────────────────

export interface ScopeItem {
  capability_id: string;
  capability_name: string;
  plan_section: string;
  related_epics: string[];
  tracker_status: BuildStatus;
  code_evidence: 'Presente' | 'Ausente' | 'Parcial';
  scope_decision: string;
  decision_evidence?: string;
  approved_by?: string;
  reentry_condition?: string;
  affected_criteria?: string[];
  caducada?: boolean;
}

export const fetchScopeItems = () => apiGet<ScopeItem[]>(`${QA}/scope-items`);

export const fetchCoverageCheck = () =>
  apiGet<{
    capacidades_en_plan: number;
    registradas: number;
    huerfanas: string[];
    puede_publicar: boolean;
  }>(`${QA}/scope-items/coverage-check`);

export const runScopeReviewCycle = () =>
  apiPost<{ reingresadas: string[]; caducadas: string[] }>(`${QA}/scope-items/review-cycle`, {});

// ── PC-07-E03 — quality gates ────────────────────────────────────────────────

export interface QualityGate {
  gate_id: string;
  gate_name: string;
  tool: string;
  workflow_file: string;
  job_name: string;
  threshold: string;
  mode: string;
  environment: string;
  platform_component: string;
  exit_criterion?: string;
  last_run_result?: string;
  advisory_since?: string;
}

export interface EnvironmentSpec {
  environment: string;
  motor_datos: string;
  bus_eventos: string;
  motor_busqueda: string;
  observabilidad: string;
  nota?: string;
}

export const fetchQualityGates = () => apiGet<QualityGate[]>(`${QA}/quality-gates`);

export const fetchGateDebt = () =>
  apiGet<{ gates_en_deuda: string[]; criterios_sin_respaldo: string[]; total: number }>(
    `${QA}/quality-gates/debt`,
  );

export const reviewGateDebt = () =>
  apiPost<{ marcados: string[] }>(`${QA}/quality-gates/debt/review`, {});

export const fetchEnvironmentSpecs = () => apiGet<EnvironmentSpec[]>(`${QA}/environment-specs`);

// ── PC-07-E04 — journeys ─────────────────────────────────────────────────────

export interface JourneyStep {
  step_order: number;
  step_description: string;
  enabling_epic: string;
  enabling_week?: number;
}

export interface Journey {
  journey_id: string;
  journey_name: string;
  roles: string[];
  steps?: JourneyStep[];
  earliest_executable_week: number;
  required_by_milestone?: string;
  milestone_week?: number;
  regression_from_week?: number;
  status: string;
  bloqueo_causa?: string;
}

export interface MilestoneConflict {
  milestone: string;
  journey_id: string;
  milestone_week: number;
  earliest_executable_week: number;
  epica_determinante: string;
  /** Every epic tied at the determining week; ties are common and both block. */
  epicas_determinantes?: string[];
}

export const fetchJourneys = () => apiGet<Journey[]>(`${QA}/journeys`);

export const recalculateJourneys = () =>
  apiPost<{ recalculados: string[] }>(`${QA}/journeys/recalculate`, {});

export const validateMilestones = (
  milestones: { milestone: string; week: number; required_journeys: string[] }[],
) =>
  apiPost<{ valido: boolean; conflictos: MilestoneConflict[]; defectos: string[] }>(
    `${QA}/milestones/validate`,
    { milestones },
  );

export const fetchRegressionSuite = (week: number) =>
  apiGet<{ semana: number; items: string[]; total: number }>(`${QA}/regression-suite/${week}`);

// ── PC-07-E05 — legacy packages ──────────────────────────────────────────────

export interface LegacyPackage {
  package_id: string;
  week: number;
  business_rules_count?: number;
  qa_folder_complete?: boolean;
  openapi_status?: string;
  gate_status: string;
}

export interface SelfCheckResult {
  package_id: string;
  criterios: { criterio: string; resultado: 'Pasa' | 'Falla'; motivo?: string }[];
  gate_status: string;
}

export const fetchLegacyPackages = () => apiGet<LegacyPackage[]>(`${QA}/legacy-packages`);

export const runSelfCheck = (packageId: string) =>
  apiPost<SelfCheckResult>(`${QA}/legacy-packages/${packageId}/selfcheck`, {});

export const fetchEntryCriterionE01 = () =>
  apiGet<{ total_epicas: number; cumplen: number; fallan: number; pendientes: string[] }>(
    `${QA}/entry-criteria/e01`,
  );

// ── PC-07-E06 — manual runbooks ──────────────────────────────────────────────

export interface ManualRunbook {
  runbook_id: string;
  epic_code: string;
  process_name: string;
  operator_role: string;
  validation_status: string;
  version?: string;
  week?: number;
}

export const fetchRunbooks = () => apiGet<ManualRunbook[]>(`${QA}/manual-runbooks`);

export const fetchManualCoverage = () =>
  apiGet<{
    total_epicas_manual: number;
    con_runbook_validado: number;
    sin_runbook: string[];
    caducados: string[];
  }>(`${QA}/manual-gate/coverage`);

export const reviewRunbookExpiry = () =>
  apiPost<{ caducados: string[] }>(`${QA}/manual-runbooks/expiry-review`, {});

// ── PC-07-E07 — thresholds ───────────────────────────────────────────────────

export interface ThresholdDecision {
  threshold_id: string;
  threshold_name: string;
  domain: string;
  estado: string;
  frozen_value?: string;
  frozen_by?: string;
  code_alignment: string;
  code_reference?: string;
  active_provider?: string;
  target_provider?: string;
  negative_check_method?: string;
}

export interface ThresholdVersion {
  threshold_id: string;
  version_id: string;
  version_value: string;
  version_source: string;
  version_status: string;
}

export const fetchThresholds = () => apiGet<ThresholdDecision[]>(`${QA}/thresholds`);

export const fetchThresholdVersions = (thresholdId: string) =>
  apiGet<ThresholdVersion[]>(`${QA}/thresholds/${thresholdId}/versions`);

export const fetchEntryCriterionE03 = () =>
  apiGet<{ en_conflicto: string[]; discrepantes: string[]; cumple: boolean }>(
    `${QA}/entry-criteria/e03`,
  );

export const setCodeAlignment = (
  thresholdId: string,
  body: { code_alignment: string; code_reference?: string; observacion?: string },
) => apiPut<ThresholdDecision>(`${QA}/thresholds/${thresholdId}/code-alignment`, body);

export const freezeThreshold = (
  thresholdId: string,
  body: {
    frozen_value: string;
    frozen_by: string;
    cantidad_maxima?: number;
    cantidad_minima?: number;
    categorias_obligatorias?: string[];
  },
) => apiPost<ThresholdDecision>(`${QA}/thresholds/${thresholdId}/freeze`, body);

// ── PC-07-E08 — oracles and baseline ─────────────────────────────────────────

export interface TestOracle {
  oracle_id: string;
  oracle_name: string;
  oracle_type: string;
  declared_in: string;
  location?: string;
  existence: string;
  version?: string;
  signed_by?: string;
  substitute_of?: string;
  estado: string;
}

export interface TrackerBaseline {
  baseline_id: string;
  baseline_hash: string;
  filas: number;
  tomado_en: string;
  baseline_signed_by?: string;
  baseline_signed_at?: string;
  vigente: boolean;
  reconciliaciones_pendientes: number;
}

export const fetchOracles = () => apiGet<TestOracle[]>(`${QA}/oracles`);

export const fetchOracleUsageCheck = () =>
  apiGet<{ casos_con_oraculo_no_verificable: string[]; total: number; puede_disenar: boolean }>(
    `${QA}/oracles/usage-check`,
  );

export const fetchBaselines = () => apiGet<TrackerBaseline[]>(`${QA}/tracker-baselines`);

export const createBaseline = () => apiPost<TrackerBaseline>(`${QA}/tracker-baselines`, {});

export const fetchReconciliations = (baselineId: string) =>
  apiGet<{ reconciled_epic: string; estado_anterior?: string; reconciled_status: string }[]>(
    `${QA}/tracker-baselines/${baselineId}/reconciliations`,
  );

export const reconcileEpic = (
  baselineId: string,
  body: {
    reconciled_epic: string;
    reconciled_status: BuildStatus;
    reconciliation_evidence: string;
  },
) => apiPost(`${QA}/tracker-baselines/${baselineId}/reconciliations`, body);

export const signBaseline = (baselineId: string, firmante: string) =>
  apiPost<TrackerBaseline>(`${QA}/tracker-baselines/${baselineId}/sign`, { firmante });

/**
 * The API answers with `{ code, message, field }` from the ERR-PC07E0N-NNN
 * catalogue, and the shared client copies `code` onto the thrown Error. QA
 * asserts on the code, so the console shows it next to the prose.
 */
export const errorCodeOf = (err: unknown): string | undefined => {
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string' && /^ERR-PC07E\d{2}-\d{3}$/.test(code)) return code;
  const message = err instanceof Error ? err.message : String(err ?? '');
  return message.match(/ERR-PC07E\d{2}-\d{3}/)?.[0];
};

export const messageOf = (err: unknown): string =>
  err instanceof Error ? err.message : 'Error inesperado';
